/**
 * Operação de DLQ (D10, §14.2).
 *
 * ── Por que o resumo não é só um número ───────────────────────────────────
 *
 * A Armadilha da task: *"medir só a profundidade. DLQ com poucos itens muito
 * antigos parece saudável e significa que ninguém olha."*
 *
 * `ResumoDaDlqSchema` carrega idade do mais velho e contagem de antigos ao
 * lado da profundidade, de propósito — para que a tela não CONSIGA mostrar só
 * o número sem mostrar a idade junto.
 */

import { z } from 'zod';

export const RAZOES_DA_DLQ = [
  'validacao',
  'permissao',
  'schema_incompativel',
  'regra_negocio',
  'limite_retry',
  'engine_indisponivel',
  'desconhecido',
] as const;

export const ResumoDaDlqSchema = z.object({
  profundidade: z.number().int(),
  /** `null` quando a fila está vazia. */
  idade_do_mais_velho_em_horas: z.number().int().nullable(),
  /** Quantos passaram de uma semana. É o número que denuncia abandono. */
  antigos: z.number().int(),
  razao_mais_comum: z.string().nullable(),
});

export const ItemDaDlqSchema = z.object({
  id: z.string().uuid(),
  event_id: z.string().uuid(),
  razao: z.string(),
  detalhe: z.string(),
  dono: z.string(),
  caminho_de_reprocessamento: z.string(),
  fonte: z.string(),
  tipo: z.string(),
  criado_em: z.string().datetime(),
  idade_em_horas: z.number().int(),
  /** Sem payload não há o que reprocessar: a purga de 90 dias o levou. */
  payload_disponivel: z.boolean(),
  tentativas: z.number().int(),
  resolvido_em: z.string().datetime().nullable(),
  reprocessado_como: z.string().uuid().nullable(),
});

export const PaginaDaDlqSchema = z.object({
  resumo: ResumoDaDlqSchema,
  /** Do mais VELHO para o mais novo — recência esconde abandono. */
  itens: z.array(ItemDaDlqSchema),
  truncado: z.boolean(),
});

export const TentativaSchema = z.object({
  numero: z.number().int(),
  de_estado: z.string().nullable(),
  para_estado: z.string(),
  desfecho: z.string(),
  codigo_do_erro: z.string().nullable(),
  detalhe_do_erro: z.string().nullable(),
  worker: z.string().nullable(),
  duracao_em_ms: z.number().int().nullable(),
  quando: z.string().datetime(),
});

export const DetalheDaDlqSchema = ItemDaDlqSchema.extend({
  /** Referenciado do `event_log`, não copiado. Nulo depois da purga. */
  payload: z.unknown(),
  tentativas_detalhadas: z.array(TentativaSchema),
});

export const ReprocessarDaDlqSchema = z
  .object({
    razao: z.enum(RAZOES_DA_DLQ).optional(),
    fonte: z.string().optional(),
    tipo: z.string().optional(),
    dono: z.string().optional(),
    desde: z.string().datetime().optional(),
    ate: z.string().datetime().optional(),
    motivo: z.string().min(10),
    /**
     * Ausente ou `true` SIMULA. Só `false` executa.
     *
     * O padrão é o seguro de propósito: lote sem simulação é como se descobre,
     * tarde, que o filtro pegou dez mil eventos.
     */
    simular: z.boolean().optional(),
  })
  .describe('Reprocessamento em lote a partir da fila de descarte');

export const ResultadoDoReprocessamentoSchema = z.object({
  simulado: z.boolean(),
  candidatos: z.number().int(),
  reenfileirados: z.array(z.object({ original: z.string(), novo: z.string() })),
  recusados: z.array(z.object({ event_id: z.string(), motivo: z.string(), detalhe: z.string() })),
  truncado: z.boolean(),
});

export const ItemResolvidoSchema = z
  .object({ resolvido: z.literal(true) })
  .describe('Confirmação da resolução manual');

export const ResolverItemDaDlqSchema = z
  .object({
    /** Por que NÃO reprocessar. Vai para a auditoria e para a coluna. */
    motivo: z.string().min(10),
  })
  .describe('Dar o item por resolvido sem reprocessar');

export type ItemDaDlq = z.infer<typeof ItemDaDlqSchema>;
export type PaginaDaDlq = z.infer<typeof PaginaDaDlqSchema>;
export type DetalheDaDlq = z.infer<typeof DetalheDaDlqSchema>;
export type ReprocessarDaDlq = z.infer<typeof ReprocessarDaDlqSchema>;
export type ResultadoDoReprocessamento = z.infer<typeof ResultadoDoReprocessamentoSchema>;
export type ResolverItemDaDlq = z.infer<typeof ResolverItemDaDlqSchema>;
